﻿import { useEffect, useRef, useState } from 'react';

import { apiUrl } from '@/helpers';

const ONLINE_PROBE_INTERVAL_MS = 10000;
const ONLINE_PROBE_TIMEOUT_MS = 4000;
const OFFLINE_CONFIRMATION_ATTEMPTS = 2;
const OFFLINE_CONFIRMATION_DELAY_MS = 300;

export const probeOnlineStatus = async (): Promise<boolean> => {
  if (!navigator.onLine) {
    return false;
  }

  const baseUrl = apiUrl();
  const probeUrl = `${baseUrl}/health`;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), ONLINE_PROBE_TIMEOUT_MS);

  try {
    const response = await fetch(probeUrl, {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal
    });

    return response.ok;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

/**
 * Reactive online/offline status hook.
 *
 * Subscribes to browser online/offline events so React components
 * re-render automatically when connectivity changes.
 *
 * Note: the axiosOfflineInterceptor also dispatches a browser 'offline'
 * event whenever any API call fails with a network error, so this hook
 * will correctly detect "WiFi up but no internet" scenarios too.
 */
export const useOnlineStatus = (): boolean => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const isOfflineVerificationInProgress = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        window.setTimeout(resolve, ms);
      });

    const reevaluateConnectivity = async () => {
      if (isOfflineVerificationInProgress.current) {
        return;
      }

      isOfflineVerificationInProgress.current = true;

      try {
        if (!navigator.onLine) {
          if (!cancelled) {
            setIsOnline(false);
          }
          return;
        }

        for (let attempt = 1; attempt <= OFFLINE_CONFIRMATION_ATTEMPTS; attempt += 1) {
          const isReachable = await probeOnlineStatus();

          if (cancelled) {
            return;
          }

          if (isReachable) {
            setIsOnline(true);
            return;
          }

          if (attempt < OFFLINE_CONFIRMATION_ATTEMPTS) {
            await wait(OFFLINE_CONFIRMATION_DELAY_MS);
          }
        }

        setIsOnline(false);
      } finally {
        isOfflineVerificationInProgress.current = false;
      }
    };

    const handleOnline = async () => {
      await reevaluateConnectivity();
    };
    const handleOffline = () => {
      // Both sources of this event are already authoritative: the browser losing its connection,
      // and axiosOfflineInterceptor, which dispatches only after probing /health and finding the
      // API unreachable. Re-probing here would keep the app "online" for several more seconds,
      // during which components render the interceptor's null-data responses as if they were real
      // payloads - which is what crashed the ceremony screen. The recovery effect below keeps
      // probing, so this flips back as soon as the backend answers again.
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    void reevaluateConnectivity();

    return () => {
      cancelled = true;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (isOnline) {
      return;
    }

    let cancelled = false;

    const tryRecoverOnlineState = async () => {
      const isReachable = await probeOnlineStatus();
      if (!cancelled && isReachable) {
        setIsOnline(true);
      }
    };

    void tryRecoverOnlineState();
    const intervalId = window.setInterval(() => {
      void tryRecoverOnlineState();
    }, ONLINE_PROBE_INTERVAL_MS);

    window.addEventListener('focus', tryRecoverOnlineState);
    document.addEventListener('visibilitychange', tryRecoverOnlineState);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', tryRecoverOnlineState);
      document.removeEventListener('visibilitychange', tryRecoverOnlineState);
    };
  }, [isOnline]);

  return isOnline;
};
